import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PhotoSlideshow = ({ photos, interval = 4200, className = "", testId = "photo-slideshow" }) => {
  const [idx, setIdx] = useState(0);
  const go = useCallback((n) => setIdx((i) => (n + photos.length) % photos.length), [photos.length]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), interval);
    return () => clearInterval(t);
  }, [photos.length, interval]);

  return (
    <div data-testid={testId} className={`relative overflow-hidden bg-[#041C3E] group ${className}`}>
      <AnimatePresence mode="popLayout">
        <motion.img
          key={idx}
          src={photos[idx].src}
          alt={photos[idx].alt}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      {/* caption gradient */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
      {photos[idx].caption && (
        <p className="absolute bottom-4 left-5 right-14 text-white text-sm font-medium leading-snug drop-shadow">
          {photos[idx].caption}
        </p>
      )}

      <button
        aria-label="Previous photo"
        data-testid={`${testId}-prev`}
        onClick={() => go(idx - 1)}
        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 text-[#041C3E] p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        aria-label="Next photo"
        data-testid={`${testId}-next`}
        onClick={() => go(idx + 1)}
        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 text-[#041C3E] p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
      >
        <ChevronRight size={18} />
      </button>

      <div className="absolute top-4 right-4 flex gap-1.5">
        {photos.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to photo ${i + 1}`}
            data-testid={`${testId}-dot-${i}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 transition-all duration-300 ${i === idx ? "w-6 bg-[#D94833]" : "w-1.5 bg-white/60"}`}
          />
        ))}
      </div>
    </div>
  );
};
